import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Printer, ClipboardList, Sparkles } from "lucide-react";
import { LockdownCountdown } from "@/components/LockdownCountdown";
import { BonusLeaderboardSection } from "@/components/BonusLeaderboardSection";
import { LeagueDisplaySettings } from "@/components/LeagueDisplaySettings";
import { LeagueEmailSettings } from "@/components/LeagueEmailSettings";
import { MyEntriesPanel } from "@/components/MyEntriesPanel";
import { SingleEntryRenamePanel } from "@/components/SingleEntryRenamePanel";
import { WatchersList } from "@/components/WatchersList";
import { CreateBracketBanner } from "@/components/CreateBracketBanner";
import { KnockoutPicksReadyBanner } from "@/components/KnockoutPicksReadyBanner";
import { BracketLockApproachingBanner } from "@/components/BracketLockApproachingBanner";
import { ActivityFeedCard } from "@/components/ActivityFeedCard";
import { BracketTwinsPanel } from "@/components/BracketTwinsPanel";
import { CollapsibleSection } from "@/components/league/CollapsibleSection";
import { ScoreboardCard } from "@/components/ScoreboardCard";
import { UniverseInlineCard } from "@/components/UniverseInlineCard";
import { useLeague, useIsWatcher } from "@/lib/league-context";

const indexSearchSchema = z.object({
  /** Leaderboard search query — filters rows by entry/bracket name + visible owner identity. */
  q: fallback(z.string().trim().max(100).optional(), undefined),
});

export const Route = createFileRoute("/_app/$league/")({
  validateSearch: zodValidator(indexSearchSchema),
  component: LeaderboardPage,
  head: () => ({
    meta: [
      { title: "Leaderboard — FIFA 2026 Bracket Challenge" },
      {
        name: "description",
        content:
          "See where every bracket stands — points, picks, bonus questions, and finals predictions across the FIFA 2026 Bracket Challenge.",
      },
      { property: "og:title", content: "Leaderboard — FIFA 2026 Bracket Challenge" },
      {
        property: "og:description",
        content: "Live standings, bonus picks, and finals predictions for every player.",
      },
    ],
  }),
});

function LeaderboardPage() {
  const { league } = Route.useParams();
  const leagueCtx = useLeague();
  const isWatcher = useIsWatcher();
  // Bump this to force the leaderboard to re-fetch after the user updates
  // their per-league display settings.
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <main className="mx-auto max-w-3xl w-full px-4 py-6 sm:py-10">
      {/* Hero */}
      <div className="mb-6 sm:mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 truncate" title={leagueCtx?.name}>
          <span className="text-muted-foreground">League: </span>
          <span className="text-primary">{leagueCtx?.name ?? "—"}</span>
        </div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="font-display text-3xl sm:text-5xl font-bold text-foreground leading-tight">
            Leaderboard
          </h1>
          <div className="flex items-center gap-2">
            {leagueCtx?.bracketLockAt &&
              new Date(leagueCtx.bracketLockAt) <= new Date() && (
                <a
                  href={`/print/${league}/all`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:text-primary"
                  title="Open print-friendly view of every bracket in a new tab"
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print all brackets
                </a>
              )}
            {leagueCtx?.id && (
              <>
                <LeagueDisplaySettings
                  leagueId={leagueCtx.id}
                  leagueName={leagueCtx.name}
                  onSaved={() => setReloadKey((k) => k + 1)}
                />
                <LeagueEmailSettings
                  leagueId={leagueCtx.id}
                  leagueName={leagueCtx.name}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Lockdown countdown(s) — sequence the two timers so users focus on the
          one that's locking next. Group lock is "Stage 1 of 2"; knockout
          shows as a muted footnote until group lock passes, then promotes. */}
      {(() => {
        const nowMs = Date.now();
        const hasGroup = !!leagueCtx?.groupStageLockAt;
        const groupUpcoming =
          hasGroup && new Date(leagueCtx!.groupStageLockAt!).getTime() > nowMs;
        const hasBracket = !!leagueCtx?.bracketLockAt;
        const tz = leagueCtx?.bracketLockTimezone;

        // Stage 1 active: big group timer + tiny knockout footnote
        if (groupUpcoming) {
          return (
            <>
              <div className="mb-2">
                <LockdownCountdown
                  lockAt={leagueCtx!.groupStageLockAt!}
                  timezone={tz}
                  variant="banner"
                  label="Stage 1 of 2 — Group picks lock in"
                  lockedLabel="Group picks are locked"
                  footer="Lock in your 1st–4th for every group before the whistle blows."
                />
              </div>
              {hasBracket && (
                <div className="mb-3 px-1 text-[11px] sm:text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="opacity-70">Stage 2 →</span>
                  <span>
                    Knockout picks unlock after group lock
                    {leagueCtx!.bracketLockAt && (
                      <>
                        {" · locks "}
                        {new Date(leagueCtx!.bracketLockAt!).toLocaleDateString(
                          undefined,
                          { month: "short", day: "numeric" },
                        )}
                      </>
                    )}
                  </span>
                </div>
              )}
            </>
          );
        }

        // Stage 1 done (or no group stage): big knockout timer.
        // Surface the group-lock timestamp as a secondary line so users still
        // see when group picks closed without consuming a whole card.
        const groupLockedFooter =
          hasGroup && leagueCtx?.groupStageLockAt
            ? `Group picks locked on ${new Date(leagueCtx.groupStageLockAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}.`
            : null;
        const bracketLockedNow =
          !!leagueCtx?.bracketLockAt &&
          new Date(leagueCtx.bracketLockAt) <= new Date();
        return (
          <>
            {hasBracket && !bracketLockedNow && (
              <div className="mb-3">
                <LockdownCountdown
                  lockAt={leagueCtx!.bracketLockAt!}
                  timezone={tz}
                  variant="banner"
                  label={hasGroup ? "Stage 2 of 2 — Knockout picks lock in" : "Bracket locks in"}
                  lockedLabel={hasGroup ? "Both stages locked" : "Bracket is locked"}
                  footer={groupLockedFooter ?? "Fill in your full bracket — every R32 match through the final."}
                />
              </div>
            )}
            {!hasBracket && hasGroup && (
              <div className="mb-3">
                <LockdownCountdown
                  lockAt={leagueCtx!.groupStageLockAt!}
                  timezone={tz}
                  variant="banner"
                  label="Stage 1 of 2 — Group picks"
                  lockedLabel="Stage 1 complete — group picks locked"
                />
              </div>
            )}
          </>
        );
      })()}
      {(leagueCtx?.groupStageLockAt || leagueCtx?.bracketLockAt) && (
        <p className="mb-6 px-1 text-[11px] sm:text-xs text-muted-foreground">
          {(() => {
            const fmt = (iso: string) =>
              new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
            const bracketLocked =
              !!leagueCtx?.bracketLockAt &&
              new Date(leagueCtx.bracketLockAt) <= new Date();
            const groupLocked =
              !!leagueCtx?.groupStageLockAt &&
              new Date(leagueCtx.groupStageLockAt) <= new Date();
            if (bracketLocked) {
              if (leagueCtx?.groupStageLockAt && leagueCtx?.bracketLockAt) {
                return `🔒 All stages locked — group picks locked ${fmt(leagueCtx.groupStageLockAt)}, knockout locked ${fmt(leagueCtx.bracketLockAt)}.`;
              }
              if (leagueCtx?.bracketLockAt) {
                return `🔒 Bracket locked ${fmt(leagueCtx.bracketLockAt)}.`;
              }
              return "🔒 Bracket is locked.";
            }
            if (groupLocked) {
              return "🔓 Group picks are now visible. Knockout picks stay private until the bracket timer hits zero.";
            }
            return "🔒 All picks stay private until each timer hits zero — then every group sheet and bracket becomes Goblin-readable. 👀";
          })()}
        </p>
      )}


      {/* Universe teaser — post-bracket-lock only. Primary mobile entry into
          the site-wide leaderboard; no new nav tab needed. */}
      {leagueCtx?.bracketLockAt &&
        new Date(leagueCtx.bracketLockAt) <= new Date() && (
          <UniverseInlineCard />
        )}

      {/* Reminder email preference now lives in the "League settings" dialog (top-right of hero). */}

      {/* Zero-entry CTA — shown for approved participants who haven't created
          a bracket yet. Watchers never see this. */}
      {leagueCtx?.id && !isWatcher && (
        <CreateBracketBanner
          leagueId={leagueCtx.id}
          leagueSlug={league}
          bracketLockAt={leagueCtx.bracketLockAt}
        />
      )}

      {/* G+K nudge: when admin has seeded R32 matches the viewer hasn't picked,
          surface a one-time-per-new-slot banner with a deep link to the bracket. */}
      <KnockoutPicksReadyBanner />

      {/* Lock-approaching nudge: when bracket lock is < 3 days away AND any
          entry is missing bonus or tiebreaker answers, surface a persistent
          amber banner with deep links to finish those brackets. */}
      <BracketLockApproachingBanner />

      {/* My entries — only shown when the league allows multiple entries per
          member AND no lock has hit yet. Once group or bracket lock passes,
          bracket names are frozen and the pinned "Your Brackets" section
          above Live Standings already lists all entries the viewer controls. */}
      {leagueCtx?.id && !isWatcher && (() => {
        const now = Date.now();
        const groupLocked =
          !!leagueCtx.groupStageLockAt &&
          new Date(leagueCtx.groupStageLockAt).getTime() <= now;
        const bracketLocked =
          !!leagueCtx.bracketLockAt &&
          new Date(leagueCtx.bracketLockAt).getTime() <= now;
        if (groupLocked || bracketLocked) return null;
        const isMulti = (leagueCtx.maxEntriesPerMember ?? 1) > 1;
        if (isMulti) {
          return (
            <div className="mb-6">
              <MyEntriesPanel
                leagueId={leagueCtx.id}
                leagueSlug={league}
                postLock={bracketLocked}
              />
            </div>
          );
        }
        // Single-entry leagues: compact rename panel for the viewer's bracket.
        if (!leagueCtx.viewerHasEntry) return null;
        return (
          <SingleEntryRenamePanel
            leagueId={leagueCtx.id}
            leagueSlug={league}
          />
        );
      })()}


      {/* Bracket comparisons — only renders when the viewer has me-centric insights */}
      {leagueCtx?.id && (
        <BracketTwinsPanel leagueId={leagueCtx.id} leagueSlug={league} />
      )}

      {/* Scoreboard — recent results + up next; silent when both empty */}
      {leagueCtx?.id && (
        <CollapsibleSection
          storageKey={`${leagueCtx.id}:scoreboard`}
          title="Scoreboard"
          icon={<ClipboardList className="h-4 w-4" />}
          subtitle="Recent results & what's up next"
        >
          <ScoreboardCard />
        </CollapsibleSection>
      )}

      {/* Activity feed — Phase 2 surface, sits directly above leaderboard */}
      {leagueCtx?.id && (
        <CollapsibleSection
          storageKey={`${leagueCtx.id}:goblin-take`}
          title="The Goblin's Take 🧌"
          icon={<Sparkles className="h-4 w-4" />}
          subtitle="Unhinged hot takes, reactions & league chatter"
        >
          <ActivityFeedCard
            leagueId={leagueCtx.id}
            leagueSlug={league}
            preLock={
              (!leagueCtx.groupStageLockAt ||
                new Date(leagueCtx.groupStageLockAt) > new Date()) &&
              (!leagueCtx.bracketLockAt ||
                new Date(leagueCtx.bracketLockAt) > new Date())
            }
          />
        </CollapsibleSection>
      )}

      {/* Live bonus standings (real data from picks + match results) */}
      {leagueCtx?.id && (
        <BonusLeaderboardSection
          key={reloadKey}
          leagueId={leagueCtx.id}
          leagueSlug={league}
        />
      )}


      {/* Watchers — read-only league members, shown below the leaderboard */}
      {leagueCtx?.id && <WatchersList leagueId={leagueCtx.id} />}
    </main>
  );
}
